import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Users.css";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import EditUserModal from "./Modal/EditUserModal";
import DeleteUserModal from "./Modal/DeleteUserModal";
import ViewCVModal from "./Modal/ViewCVModal";
import AxiosUrl from "../AxiosUrl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import ImageIcon from "@mui/icons-material/Image";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import useMediaQuery from "@mui/material/useMediaQuery";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { 
  getStatusDisplayText,
  getDateDisplayText, 
  getGenderDisplayText,
  getPaymentDisplayText,
  getPhotoDisplayText,
  calculateProfileCompletion,
  getProfileCompletionClass,
  getPhotoApprovalText,
  getPhotoApprovalColor,
  getUserStatusText,
  getUserStatusColor
} from "./Helper/User";

function Users() {
  const uid = localStorage.getItem("id");
  const [users, setUsers] = useState([]);
  const [open, setOpenModal] = useState(false);
  const [edit, setEdit] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [askPaymentFilter, setAskPaymentFilter] = useState("");
  const [isPhotoApprovedFilter, setIsPhotoApprovedFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");

  // Click-outside counters
  const [statusClicks, setStatusClicks] = useState(0);
  const [dateClicks, setDateClicks] = useState(0);
  const [genderClicks, setGenderClicks] = useState(0);
  const [paymentClicks, setPaymentClicks] = useState(0);
  const [photoClicks, setPhotoClicks] = useState(0);
  const [platformClicks, setPlatformClicks] = useState(0);
  
  // CV Modal state
  const [viewCVModalOpen, setViewCVModalOpen] = useState(false);
  const [selectedUserCV, setSelectedUserCV] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  
  // Currently open menu
  const [openMenu, setOpenMenu] = useState(null);

  // Media queries for responsive design
  const isMobile = useMediaQuery('(max-width:880px)');
  const isSmallMobile = useMediaQuery('(max-width:480px)');
  
  // States for DeleteUserModal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // View mode: 'grid' or 'list' - default to list
  const [viewMode, setViewMode] = useState('list');
  
  // Reset the click counter when a different menu is opened
  const handleMenuOpen = (menu) => {
    setOpenMenu(menu);
    if (menu !== 'status') setStatusClicks(0);
    if (menu !== 'date') setDateClicks(0);
    if (menu !== 'gender') setGenderClicks(0);
    if (menu !== 'payment') setPaymentClicks(0);
    if (menu !== 'photo') setPhotoClicks(0);
    if (menu !== 'platform') setPlatformClicks(0);
  };

  useEffect(() => {
    if (!open && !deleteModalOpen) {
      getUsers();
    }
  }, [
    refresh,
    open,
    deleteModalOpen,
    page,
    searchTerm,
    filterStatus,
    dateFilter,
    genderFilter,
    askPaymentFilter,
    isPhotoApprovedFilter,
    platformFilter,
  ]);

  const getUsers = () => {
    setLoading(true);
    AxiosUrl.post(`/fetch-profile/${uid}`, {
      page,
      limit,
      search: searchTerm,
      status: filterStatus,
      date: dateFilter,
      gender: genderFilter,
      askPayment: askPaymentFilter, // Send askPaymentFilter to API
      isPhotoApprovedFilter,
      platform: platformFilter,
    })
      .then((res) => {
        // Backend sends { Error } with HTTP 200 on a transient DB blip.
        // Guard against it so we don't silently blank the table — keep the
        // existing rows and surface a toast instead.
        if (res.data && Array.isArray(res.data.users)) {
          setUsers(res.data.users);
          setTotalPages(res.data.totalPages || 1);
        } else {
          toast.error(
            res.data?.Error || "Could not load users. Please try again."
          );
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        toast.error(
          "Could not load users — please check the connection and try again."
        );
        setLoading(false);
      });
  };

  const deleteUser = (userData) => {
    setUserToDelete(userData);
    setDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    setRefresh((prev) => prev + 1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
    setPage(1);
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
    setPage(1);
  };

  const handleGenderFilterChange = (event) => {
    setGenderFilter(event.target.value);
    setPage(1);
  };

  const handleAskPaymentChange = (event) => {
    setAskPaymentFilter(event.target.value);
    setPage(1);
  };
  const handleIsPhotoApprovedChange = (event) => {
    setIsPhotoApprovedFilter(event.target.value);
    setPage(1);
  };

  const handlePlatformFilterChange = (event) => {
    setPlatformFilter(event.target.value);
    setPage(1);
  };

  const getPlatformDisplayText = (value) => {
    if (value === "web") return "Web";
    if (value === "mobile") return "Mobile";
    return "All";
  };

  const handleEdit = (userId) => {
    setLoading(true);
    // Use the new endpoint to fetch complete user data
    AxiosUrl.post(`/fetch-user/${uid}/${userId}`)
      .then(res => {
        if (res.data.success && res.data.user) {
          setEdit(res.data.user);
          setOpenModal(true);
        } else {
          toast.error(res.data.error || "Failed to fetch user data");
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching user:", err);
        toast.error("Failed to fetch user data");
        setLoading(false);
      });
  };

  return (
    <div className="users-container">
      <ToastContainer />
      {open && (
        <EditUserModal
          setOpenModal={setOpenModal}
          data={edit}
          setRefresh={setRefresh}
        />
      )}
      {deleteModalOpen && userToDelete && (
        <DeleteUserModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          userData={userToDelete}
          onSuccess={handleDeleteSuccess}
        />
      )}
      {/* View CV Modal */}
      <ViewCVModal
        open={viewCVModalOpen}
        onClose={() => setViewCVModalOpen(false)}
        userId={selectedUserCV}
        userName={selectedUserName}
      />
      <div className="users-main">
        <div className="users-content-wrapper">
          <div className="users-filter-section">
            <div>
              <label className="users-filter-label" style={{ backgroundColor: '#e91e63' }}>Search</label>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="users-search-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            
            <ClickAwayListener onClickAway={() => {
              if (openMenu === 'status') {
                if (statusClicks > 0) {
                  setOpenMenu(null);
                } else {
                  setStatusClicks(statusClicks + 1);
                }
              }
            }}>
              <div>
                <label className="users-filter-label" style={{ backgroundColor: '#d58b32' }}>Status</label>
                <TextField
                  select
                  value={filterStatus}
                  onChange={handleFilterChange}
                  variant="outlined"
                  size="small"
                  className="users-status-filter"
                  onClick={() => handleMenuOpen('status')}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 }
                      }
                    },
                    displayEmpty: true,
                    renderValue: () => getStatusDisplayText(filterStatus)
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="deactive">Deactive</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>
            
            <ClickAwayListener onClickAway={() => {
              if (openMenu === 'date') {
                if (dateClicks > 0) {
                  setOpenMenu(null);
                } else {
                  setDateClicks(dateClicks + 1);
                }
              }
            }}>
              <div>
                <label className="users-filter-label" style={{ backgroundColor: '#388e3c' }}>Date</label>
                <TextField
                  select
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  variant="outlined"
                  size="small"
                  className="users-date-filter"
                  onClick={() => handleMenuOpen('date')}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 }
                      }
                    },
                    displayEmpty: true,
                    renderValue: () => getDateDisplayText(dateFilter)
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="lastMonth">Last Month</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>
            
            <ClickAwayListener onClickAway={() => {
              if (openMenu === 'gender') {
                if (genderClicks > 0) {
                  setOpenMenu(null);
                } else {
                  setGenderClicks(genderClicks + 1);
                }
              }
            }}>
              <div>
                <label className="users-filter-label" style={{ backgroundColor: '#3f51b5' }}>Gender</label>
                <TextField
                  select
                  value={genderFilter}
                  onChange={handleGenderFilterChange}
                  variant="outlined"
                  size="small"
                  className="users-gender-filter"
                  onClick={() => handleMenuOpen('gender')}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 }
                      }
                    },
                    displayEmpty: true,
                    renderValue: () => getGenderDisplayText(genderFilter)
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Male">Men</MenuItem>
                  <MenuItem value="Female">Women</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>

            <ClickAwayListener onClickAway={() => {
              if (openMenu === 'platform') {
                if (platformClicks > 0) {
                  setOpenMenu(null);
                } else {
                  setPlatformClicks(platformClicks + 1);
                }
              }
            }}>
              <div>
                <label className="users-filter-label" style={{ backgroundColor: '#2e7d32' }}>Platform</label>
                <TextField
                  select
                  value={platformFilter}
                  onChange={handlePlatformFilterChange}
                  variant="outlined"
                  size="small"
                  className="users-platform-filter"
                  onClick={() => handleMenuOpen('platform')}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 }
                      }
                    },
                    displayEmpty: true,
                    renderValue: () => getPlatformDisplayText(platformFilter)
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="web">Web</MenuItem>
                  <MenuItem value="mobile">Mobile</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>

            <ClickAwayListener onClickAway={() => {
              if (openMenu === 'payment') {
                if (paymentClicks > 0) {
                  setOpenMenu(null);
                } else {
                  setPaymentClicks(paymentClicks + 1);
                }
              }
            }}>
              <div>
                <label className="users-filter-label" style={{ backgroundColor: '#7b1fa2' }}>Ask Payment</label>
                <TextField
                  select
                  value={askPaymentFilter}
                  onChange={handleAskPaymentChange}
                  variant="outlined"
                  size="small"
                  className="users-payment-filter"
                  onClick={() => handleMenuOpen('payment')}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 }
                      }
                    },
                    displayEmpty: true,
                    renderValue: () => getPaymentDisplayText(askPaymentFilter)
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>
            
            <ClickAwayListener onClickAway={() => {
              if (openMenu === 'photo') {
                if (photoClicks > 0) {
                  setOpenMenu(null);
                } else {
                  setPhotoClicks(photoClicks + 1);
                }
              }
            }}>
              <div>
                  <label className="users-filter-label" style={{ backgroundColor: '#1976d2' }}>IsPhotoApproved</label>
                  <TextField
                  select
                  value={isPhotoApprovedFilter}
                  onChange={handleIsPhotoApprovedChange}
                  variant="outlined"
                  size="small"
                  className="users-photo-filter"
                  onClick={() => handleMenuOpen('photo')}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 }
                      }
                    },
                    displayEmpty: true,
                    renderValue: () => getPhotoDisplayText(isPhotoApprovedFilter)
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>
          </div>

          {/* View Toggle Buttons */}
          <div className="users-view-toggle">
            <IconButton
              onClick={() => setViewMode('grid')}
              color={viewMode === 'grid' ? 'primary' : 'default'}
              title="Grid View"
              style={{
                backgroundColor: viewMode === 'grid' ? '#1976d2' : '#f5f5f5',
                color: viewMode === 'grid' ? 'white' : '#666',
                marginRight: '8px'
              }}
            >
              <ViewModuleIcon />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
              title="List View"
              style={{
                backgroundColor: viewMode === 'list' ? '#1976d2' : '#f5f5f5',
                color: viewMode === 'list' ? 'white' : '#666'
              }}
            >
              <ViewListIcon />
            </IconButton>
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="users-grid-container">
              {loading && <div className="google-progress-bar"></div>}
              {users.length <= 0 && !loading ? (
                <div className="users-no-data-available">
                  No data available
                </div>
              ) : (
                <div className="users-grid">
                  {users.map((data) => (
                    <Card key={data._id} className="users-grid-card">
                      <CardContent className="users-grid-card-content">
                        <div className="users-grid-header">
                          <div className="users-grid-img-container">
                            {data.photo ? (
                              <img
                                src={data.photo}
                                alt={data.username}
                                className="users-grid-img"
                              />
                            ) : (
                              <div className="users-grid-img-placeholder">
                                <ImageIcon />
                              </div>
                            )}
                            <div 
                              className="users-status-dot" 
                              style={{ backgroundColor: getUserStatusColor(data.userStatus) }}
                              title={getUserStatusText(data.userStatus)}
                            />
                          </div>
                          <div className="users-grid-header-info">
                            <Typography variant="h6" className="users-grid-name">
                              {data.username}
                            </Typography>
                            {data.source && (
                              <Chip
                                label={data.source}
                                size="small"
                                className="users-grid-source-chip"
                                style={{
                                  backgroundColor: 
                                    data.source === "facebook" ? "#1877f2" : 
                                    data.source === "google" ? "#ea4335" :
                                    data.source === "direct" ? "#9e9e9e" :
                                    data.source === "organic" ? "#4caf50" :
                                    data.source === "affiliate" ? "#ff9800" :
                                    data.source === "referral" ? "#9c27b0" :
                                    data.source === "instagram" ? "#e4405f" :
                                    data.source === "twitter" ? "#1da1f2" :
                                    data.source === "youtube" ? "#ff0000" :
                                    data.source === "linkedin" ? "#0077b5" : "#607d8b",
                                  color: "white",
                                  fontSize: "11px",
                                  height: "26px",
                                  fontWeight: "600",
                                  textTransform: "capitalize",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                  display: "inline-flex",
                                  visibility: "visible",
                                  opacity: 1
                                }}
                              />
                            )}
                            {!data.source && (
                              <Chip
                                label="N/A"
                                size="small"
                                className="users-grid-source-chip"
                                style={{
                                  backgroundColor: "#607d8b",
                                  color: "white",
                                  fontSize: "11px",
                                  height: "26px",
                                  fontWeight: "600",
                                  textTransform: "capitalize",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                  display: "inline-flex",
                                  visibility: "visible",
                                  opacity: 1
                                }}
                              />
                            )}
                          </div>
                        </div>
                        
                        <div className="users-grid-details">
                          <div className="users-grid-detail-item">
                            <Typography variant="caption" className="users-grid-label">Photo Approved:</Typography>
                            <Chip
                              label={getPhotoApprovalText(data.isPhotoApproved)}
                              size="small"
                              style={{
                                backgroundColor: getPhotoApprovalColor(data.isPhotoApproved),
                                color: "white",
                                fontSize: "11px",
                                height: "26px",
                                fontWeight: "600",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                              }}
                            />
                          </div>
                          
                          {!isSmallMobile && (
                            <div className="users-grid-detail-item">
                              <Typography variant="caption" className="users-grid-label">Profile:</Typography>
                              <Chip
                                label={calculateProfileCompletion(data) + "%"}
                                size="small"
                                className={getProfileCompletionClass(calculateProfileCompletion(data))}
                                style={{ 
                                  minWidth: "70px", 
                                  height: "26px", 
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                  color: "white",
                                  whiteSpace: "nowrap",
                                  overflow: "visible",
                                  textOverflow: "clip"
                                }}
                              />
                            </div>
                          )}
                          
                          <div className="users-grid-detail-item">
                            <Typography variant="caption" className="users-grid-label">Package:</Typography>
                            <Chip
                              label={data.subscription ? `৳${data.subscription.Amount}` : '৳0'}
                              size="small"
                              style={{
                                backgroundColor: "#9575cd",
                                color: "white",
                                fontSize: "12px",
                                height: "26px",
                                fontWeight: "600",
                                boxShadow: "0 2px 4px rgba(149, 117, 205, 0.3)"
                              }}
                            />
                          </div>
                          
                          <div className="users-grid-detail-item">
                            <Typography variant="caption" className="users-grid-label">Balance:</Typography>
                            <Chip
                              label={`৳${data.Balance || 0}`}
                              size="small"
                              style={{
                                backgroundColor: "#26a69a",
                                color: "white",
                                fontSize: "12px",
                                height: "26px",
                                fontWeight: "600",
                                boxShadow: "0 2px 4px rgba(38, 166, 154, 0.3)"
                              }}
                            />
                          </div>

                          <div className="users-grid-detail-item">
                            <Typography variant="caption" className="users-grid-label">PushToken:</Typography>
                            <Chip
                              label={data.ExpoToken || data.FcmPushToken ? "True" : "False"}
                              size="small"
                              style={{
                                backgroundColor: data.ExpoToken || data.FcmPushToken ? "#4caf50" : "#f44336",
                                color: "white",
                                fontSize: "12px",
                                height: "26px",
                                fontWeight: "600",
                                boxShadow: data.ExpoToken || data.FcmPushToken
                                  ? "0 2px 4px rgba(76, 175, 80, 0.3)"
                                  : "0 2px 4px rgba(244, 67, 54, 0.3)"
                              }}
                            />
                          </div>
                        </div>
                      </CardContent>
                      <CardActions className="users-grid-actions">
                        <Button
                          className="users-edit-button"
                          onClick={() => handleEdit(data._id)}
                          size="small"
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                        <Button
                          className="users-cv-button"
                          onClick={() => {
                            setSelectedUserCV(data._id);
                            setSelectedUserName(data.username);
                            setViewCVModalOpen(true);
                          }}
                          size="small"
                          startIcon={<DescriptionIcon />}
                        >
                          CV
                        </Button>
                        <Button
                          className="users-delete-button"
                          onClick={() => deleteUser(data)}
                          size="small"
                          startIcon={<DeleteIcon />}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* List/Table View */}
          {viewMode === 'list' && (
          <TableContainer 
            component={Paper}
            className="users-table-container"
          >
            {loading && <div className="google-progress-bar"></div>}
            <Table className="users-table" aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    Image
                  </TableCell>
                  <TableCell align="left">
                    isPhotoApproved
                  </TableCell>
                  {!isSmallMobile && (
                    <TableCell align="center">
                      Success
                    </TableCell>
                  )}
                  <TableCell align="left">
                    Name
                  </TableCell>
                  <TableCell align="center">
                    Package
                  </TableCell>
                  <TableCell align="center">
                    Balance
                  </TableCell>
                  {!isMobile && (
                    <TableCell align="center">
                      Source
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell align="center">
                      Platform
                    </TableCell>
                  )}
                  {!isMobile && (
                    <>
                      <TableCell align="center">
                        Actions
                      </TableCell>
                    </>
                  )}
                  {isMobile && (
                    <TableCell align="center">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length <= 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? (isSmallMobile ? 5 : 6) : 10} align="center">
                      <div className="users-no-data-available">
                        No data available
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((data) => (
                    <TableRow
                      className="users-table-row"
                      key={data._id}
                    >
                      <TableCell
                        align="left"
                        component="th"
                        scope="row"
                        className="users-img-cell"
                      >
                        <div className="users-img-container">
                          {data.photo ? (
                            <img
                              src={data.photo}
                              alt={data.username}
                              className="users-profile-img"
                            />
                          ) : (
                            <div className="users-img-placeholder">
                              <ImageIcon style={{ fontSize: "24px" }} />
                            </div>
                          )}
                          <div 
                            className="users-status-dot" 
                            style={{ backgroundColor: getUserStatusColor(data.userStatus) }}
                            title={getUserStatusText(data.userStatus)}
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        align="left"
                        component="th"
                        scope="row"
                        className="users-photo-approval-cell"
                      >
                        <Button 
                          className="users-photo-approval-button"
                          style={{
                            backgroundColor: getPhotoApprovalColor(data.isPhotoApproved)
                          }}
                        >
                          {getPhotoApprovalText(data.isPhotoApproved)}
                        </Button>
                      </TableCell>
                      {!isSmallMobile && (
                        <TableCell
                          align="center"
                          component="th"
                          scope="row"
                          className="users-profile-cell"
                        >
                          <Button
                            className={getProfileCompletionClass(calculateProfileCompletion(data))}
                          >
                            {calculateProfileCompletion(data) + "%"}
                          </Button>
                        </TableCell>
                      )}
                      <TableCell
                        align="left"
                        component="th"
                        scope="row"
                        className="users-name-cell"
                      >
                        <Button 
                          className="users-name-button"
                        >
                          {data.username}
                        </Button>
                      </TableCell>
                      
                      <TableCell align="center" component="th" scope="row">
                        <Button
                          className="users-package-button"
                        >
                          {data.subscription ? `৳${data.subscription.Amount}` : '৳0'}
                        </Button>
                      </TableCell>

                      <TableCell align="center" component="th" scope="row">
                        <Button
                          className="users-balance-button"
                        >
                          ৳{data.Balance || 0}
                        </Button>
                      </TableCell>
                      
                      {!isMobile && (
                        <TableCell align="center" component="th" scope="row">
                          <Button
                            className="users-source-button"
                            style={{
                              backgroundColor:
                                data.source === "facebook" ? "#1877f2" :
                                data.source === "google" ? "#ea4335" :
                                data.source === "direct" ? "#9e9e9e" :
                                data.source === "organic" ? "#4caf50" :
                                data.source === "affiliate" ? "#ff9800" :
                                data.source === "referral" ? "#9c27b0" :
                                data.source === "instagram" ? "#e4405f" :
                                data.source === "twitter" ? "#1da1f2" :
                                data.source === "youtube" ? "#ff0000" :
                                data.source === "linkedin" ? "#0077b5" : "#607d8b",
                              color: "white",
                              fontWeight: "500",
                              fontSize: "12px",
                              padding: "4px 12px",
                              textTransform: "capitalize",
                            }}
                          >
                            {data.source || "N/A"}
                          </Button>
                        </TableCell>
                      )}

                      {!isMobile && (
                        <TableCell align="center" component="th" scope="row">
                          {(() => {
                            // data.platform is "web" for web signups; "mobile"
                            // for new mobile signups (backend default); and
                            // null/undefined for legacy users whose signup
                            // predates the field.
                            const isWeb = data.platform === "web";
                            const known = data.platform === "web" || data.platform === "mobile";
                            return (
                              <Button
                                className="users-platform-button"
                                style={{
                                  backgroundColor: isWeb
                                    ? "#2e7d32"
                                    : known
                                    ? "#455a64"
                                    : "#9e9e9e",
                                  color: "white",
                                  fontWeight: "500",
                                  fontSize: "12px",
                                  padding: "4px 12px",
                                  textTransform: "capitalize",
                                  minWidth: "72px",
                                }}
                              >
                                {isWeb ? "Web" : known ? "Mobile" : "Unknown"}
                              </Button>
                            );
                          })()}
                        </TableCell>
                      )}

                      {!isMobile && (
                        <>
                          <TableCell align="center" component="th" scope="row">
                            <div className="users-action-buttons">
                              <Button
                                className="users-edit-button"
                                onClick={() => {
                                  handleEdit(data._id);
                                }}
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </Button>
                              <Button
                                className="users-cv-button"
                                onClick={() => {
                                  setSelectedUserCV(data._id);
                                  setSelectedUserName(data.username);
                                  setViewCVModalOpen(true);
                                }}
                                size="small"
                                style={{ marginLeft: '8px' }}
                              >
                                <DescriptionIcon fontSize="small" />
                              </Button>
                              <Button
                                className="users-delete-button"
                                onClick={() => deleteUser(data)}
                                size="small"
                                style={{ marginLeft: '8px' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                      
                      {isMobile && (
                        <TableCell align="center" className="users-action-cell">
                          <div className="users-mobile-actions">
                            <Button
                              className="users-edit-button"
                              onClick={() => {
                                handleEdit(data._id);
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </Button>
                            <Button
                              className="users-cv-button"
                              onClick={() => {
                                setSelectedUserCV(data._id);
                                setSelectedUserName(data.fullName || data.name || "User");
                                setViewCVModalOpen(true);
                              }}
                              size="small"
                              style={{ marginLeft: '5px' }}
                            >
                              <DescriptionIcon fontSize="small" />
                            </Button>
                            <Button
                              className="users-delete-button"
                              onClick={() => deleteUser(data)}
                              size="small"
                              style={{ marginLeft: '5px' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          
          {/* Pagination - shown for both views */}
          <div className="users-pagination-container">
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              size="medium"
              siblingCount={1}
              boundaryCount={1}
              shape="rounded"
              className="users-pagination"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Users;
